import Phaser from "phaser";
import { WalkChapter } from "./WalkChapter";
import { TORRE } from "../data/torre";
import type { ChapterData } from "../data/content-types";
import { isoDepth, TILE_H } from "../core/iso";
import * as P from "../art/props";

/** CAPÍTULO 4 — "Turno de noche". Oficina nocturna: fondo, suelo y decorado propios. */
export class TorreScene extends WalkChapter {
  protected chapter: ChapterData = TORRE;
  protected grid = { w: 15, h: 11 };
  protected spawn = { x: 8, y: 9 };
  private decor: Phaser.GameObjects.GameObject[] = [];

  constructor() {
    super("torre");
  }

  protected floorKind(): P.TileKind {
    return "planks"; // parquet de oficina
  }

  // NPC de oficina (sin casco ni chaleco)
  protected buildInteractableArt(def: import("../core/types").InteractableDef): Phaser.GameObjects.GameObject[] {
    if (def.type === "npc") return [P.officeperson(this, { shirt: def.color })];
    return super.buildInteractableArt(def);
  }

  protected buildBackdrop(): void {
    const cam = this.cameras.main;
    cam.setBackgroundColor(0x2b3242);
    const W = 1280, H = 720, over = 420;
    const sky = this.add.graphics().setScrollFactor(0).setDepth(-19000);
    sky.fillGradientStyle(0x3a4763, 0x3a4763, 0x232a3a, 0x232a3a, 1);
    sky.fillRect(-over, -over, W + over * 2, H * 0.62 + over);
    // skyline nocturno con ventanas encendidas
    const sk = this.add.graphics().setScrollFactor(0.25).setDepth(-18900);
    const base = H * 0.46;
    for (const [x, w, ht] of [[40, 120, 150], [180, 90, 100], [290, 150, 200], [470, 100, 130], [900, 130, 170], [1050, 170, 220], [1230, 90, 120]] as [number, number, number][]) {
      sk.fillStyle(0x1c2230, 0.95); sk.fillRect(x, base - ht, w, ht);
      for (let wy = base - ht + 10; wy < base - 8; wy += 15) for (let wx = x + 6; wx < x + w - 6; wx += 13) { sk.fillStyle(((wx + wy) % 3 === 0) ? 0xffe6a0 : 0x2a3242, 0.9); sk.fillRect(wx, wy, 7, 8); }
    }
    // luna
    sk.fillStyle(0xdfe4e6, 0.7); sk.fillCircle(680, base - 210, 26);
    // antepecho de oficina (pared baja); el skyline queda como ventanal por encima
    this.buildWalls({ left: 0x2c3444, right: 0x3a4658, height: 88, base: 0x222a38, texture: "plain" });
  }

  protected buildDecor(): void {
    this.decor.forEach((d) => d.destroy());
    this.decor = [];
    const put = (obj: Phaser.GameObjects.Container, x: number, y: number): void => {
      const p = this.iso(x, y);
      obj.setPosition(p.sx, p.sy).setDepth(isoDepth(x, y));
      this.decor.push(obj);
    };
    // puestos de trabajo (mesa + silla) por las esquinas
    const station = (x: number, y: number, chair = 0x2c6270) => { put(P.desk(this), x, y); put(P.officechair(this, chair), x - 0.1, y + 0.8); };
    station(2.6, 2.6); station(11.6, 2.6, 0x8a5a3a);
    station(2.6, 6.2, 0x3a5a8a); station(12.2, 6.4);
    station(2.6, 9.2, 0x5a5f68);
    // plantas + archivadores
    put(P.plant(this), 1.4, 4.4); put(P.plant(this), 13.4, 4.4); put(P.plant(this), 7.4, 1.6);
    put(P.locker(this), 13.4, 8.8);

    // --- mobiliario de oficina (densidad) ---
    put(P.filecabinet(this), 7.0, 1.4); put(P.filecabinet(this, 0x6f757c), 7.7, 1.5);
    put(P.filecabinet(this), 13.6, 2.0);
    put(P.watercooler(this), 1.4, 8.4);
    put(P.printer(this), 9.4, 1.7);
    put(P.plant(this), 10.6, 8.8);
    put(P.desk(this), 8.2, 4.0); put(P.officechair(this, 0x3a5a8a), 8.1, 4.8);

    // paneles de techo (oficina iluminada de noche)
    for (const [lx, ly] of [[4, 3], [9, 3], [4, 7], [10, 6.5]] as [number, number][]) {
      const p = this.iso(lx, ly);
      const pg = this.add.graphics().setDepth(-9300); const pcy = p.sy + TILE_H / 2; pg.fillStyle(0xffe0a0, 0.13); pg.fillEllipse(p.sx, pcy, 80, 38); pg.fillStyle(0xfff2c8, 0.12); pg.fillEllipse(p.sx, pcy, 45, 22); this.decor.push(pg); this.addPulse(pg, 0.11, 0.005, 0.88);
      const panel = P.ceilingPanel(this, 150); panel.setPosition(p.sx, p.sy).setDepth(50000); this.decor.push(panel);
    }
    if (this.state.chapter("torre").done) this.onResolved();
  }

  protected onResolved(): void {
    const p = this.iso(8, 2);
    this.add.text(p.sx, p.sy - 30, "puesto ajustado", { fontSize: "10px", color: "#1e5b38", fontStyle: "bold", backgroundColor: "#eafaf0", padding: { x: 4, y: 2 } }).setOrigin(0.5).setDepth(70);
  }
}
